<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div ref="wyTableList" class="wy-table-list targets" v-loading="loading">
    <div :class="(tableData && tableData.length > 0 && iShowTable? 'wy-table-wrapper':'wy-table-wrapper no-data-table-wrapper')" style="width: 100%; overflow-x: hidden;">
      <div v-if="tableData && tableData.length > 0 && iShowTable">
        <!-- Main columns table -->
        <table border="0" class="wy-table-main">
          <thead>          
            <tr ref="wyHeaderMain" class="wy-header" :style="{'left': scrollLeft + 'px'}">
              <th v-for="(group, index) in groupColumns" :key="index" :colspan="group.headerSpan" 
                  v-bind:style="{'min-width': (group.mainColumn?variableColWidth:group.headerWith)+'px !important'}">
                <span v-if="group.type!='index' && group.type!='selection' && group.name !== 'target'">{{group.label}}</span>
                <span v-else-if="group.type!='index' && group.type!='selection' && tableData && tableData.length > 0 && iShowTable" >{{group.label}}</span>
              </th>
              <th class="wy-table-tail"><br></th>
            </tr>
            <tr ref="wySubheaderMain" class="wy-sub-header" :style="{'left': scrollLeft + 'px'}">
              <th v-for="(column, index) in allColumns" :key="index" 
                  v-bind:class="{'lastChild': column.lastChild, 'sortable': sortableColumn(column), 'sorted': sortedColumnName==column.sortBy,'ascending': sortedType^sortCaretHovered==0, 'descending': sortedType^sortCaretHovered==1}" 
                  v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px !important'}" @click="handleSort(column)" 
                  @mouseenter="toggleSortCaretHovered(column.sortBy)" @mouseleave="toggleSortCaretHovered(column.sortBy)">
                <span v-if="column.type!='index'">{{column.label}}</span>
              </th>
              <th class="wy-table-tail"><br></th>
            </tr>        
          </thead>
          <tbody  ref="wyTableBodyMain" class="wy-body" v-bind:class="{'isTableEditing': isTableEditing}" @mousewheel="handleMousewheelOnMainTable" @scroll="handleScrollingOnMainTable">
            <tr v-for="(row, index) in tableData" :key="index" @click="handleClick($event, index, row)" @mouseover="handleMouseover" @mouseleave="handleMouseleave" :class="{'highlightedRow': index == highlightedIndex, 'clickable': isClickable}">
              <td v-for="(column, columnIndex) in allColumns" :key="columnIndex" v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px', 'text-align': column.align}" 
                  :class="{'last-child': column.lastChild}">
                <!-- empty column (if it's part of the fixed columns) since it will be rendered in fixed column -->
                <div v-if="column.customCell">
                  <component :is="column.customCell" v-bind="{controller: controller, column: column, row: row, index: index}"></component>
                </div>
                <div v-else-if="column.fixed"></div>
                <!-- index column -->
                <div v-else-if="column.type=='index'"></div>
                <!-- icon column -->
                <div v-else-if="column.type=='icon'"></div>
                <!-- html column -->
                <div v-else-if="column.type=='html'">
                  <div v-html="row[column.name]"></div>
                </div>
                <!-- trend column -->
                <div v-else-if="column.type=='trend'">
                  <div v-html="row['trendChart']"></div>
                </div>
                <div v-else-if="column.name=='actionItem'">
                  <div v-if="row[column.name]==null || row[column.name]==false"> - </div>
                  <div v-else class="is-action-item" :class="{'red': checkGreaterThanThreshold(row), 'yellow': !checkGreaterThanThreshold(row)}"></div>   
                </div>
                <!-- no data column -->
                <div v-else-if="row[column.name]==null || row[column.name]=='NaN' || row[column.name]=='+Infinity' || row[column.name]==-1">
                  <div v-if="column.type=='percentChange'" class="el-no-data" @mouseover="handleTooltipOver($event, noDataTooltipForPercentChange)" @mouseout="handleTooltipOut($event)">N/A</div>
                  <div v-else-if="column.type=='target'" class="el-no-data" @mouseover="handleTooltipOver($event, noDataTooltipForTarget)" @mouseout="handleTooltipOut($event)">N/A</div>
                  <div v-else class="el-no-data" @mouseover="handleTooltipOver($event, (column.noDataTooltip ? column.noDataTooltip : noDataTooltipContent))" @mouseout="handleTooltipOut($event)">N/A</div>
                </div>
                <!-- % change column -->
                <div @mouseover="handleTooltipOver($event, row[column.tooltipMessage])" @mouseout="handleTooltipOut($event)"  :class="{'arrow-up positive-change-style':row[column.name]>0,'arrow-down negative-change-style':row[column.name]<0,'zero-change-style':row[column.name]==0, 'no-data-tooltip-style': row[column.name] == undefined || row[column.name] == null}" v-else-if="column.type=='percentChange'">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-if="row[column.name]==0" class="zero-change">0.00</span>
                  <span v-else-if="row[column.name] == undefined || row[column.name] == null" class="zero-change">N/A</span>
                  <span v-else v-html="cellFormatter(Math.abs(row[column.name]))" :class="{'positive-change':row[column.name]>0,'negative-change':row[column.name]<0 }"></span>

                  <span v-if="column.suffix">{{column.suffix}}</span>                
                </div>
    
                <!-- tooltip column -->
                <div v-else-if="column.tooltipMessage && row[column.tooltipMessage]" @mouseover="handleTooltipOver($event, row[column.tooltipMessage])" @mouseout="handleTooltipOut($event)">
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="cellFormatter(row[column.name])"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
                <!-- bes & bas targets -->
                <div v-if="column.type=='targetRange'">
                  <wy-target-number-range :ref="`numberComponent${column.name+index}`" v-if="row[column.name]&&Object.keys(row[column.name]).length>0" :controller="module" :rowIndex="index" :tableData="tableData" :columnName="column.name" :data="tableData[index][column.name]" :value="row[column.name].value" :type="column.type" />
                  <span class="cancel-target el-icon-close" @click="removeTargetValue(row[column.name],$refs['numberComponent'+column.name+index])"></span>
                </div>
                <div v-else-if="column.type=='targetWithReverse'">
                  <input type="checkbox" v-if="row[column.name]&&Object.keys(row[column.name]).length>0" :disabled="!row[column.name].value" :checked="row[column.name].reverse" @change="handleReverseChange($event, row[column.name])"/> <span class="reverse-text">Reverse value</span>
                  <wy-target-number-range v-if="row[column.name]&&Object.keys(row[column.name]).length>0" :controller="module" :rowIndex="index" :tableData="tableData" :columnName="column.name" :data="tableData[index][column.name]" :value="row[column.name].value" />
                  <span class="company-target-suffix">
                    <span v-if="row[column.name]&&row[column.name].suffix">{{row[column.name].suffix}}</span>
                  </span>
                  <span class="cancel-target el-icon-close" @click="removeTargetValue(row[column.name])"></span>
                    
                </div>
                <div v-else-if="column.type=='targetWithOperator'">
                  <wy-target-operator v-if="row[column.name]" :controller="module" :operator="row[column.name].operator" :targetData="row[column.name]" />
                  <wy-target-number-range v-if="row[column.name]&&Object.keys(row[column.name]).length>0" :controller="module" :rowIndex="index" :tableData="tableData" :columnName="column.name" :data="tableData[index][column.name]" :value="row[column.name].value" />
                  <span class="company-target-suffix">
                    <span v-if="row[column.name]&&row[column.name].suffix">{{row[column.name].suffix}}</span>
                  </span>
                  <span class="cancel-target el-icon-close" @click="removeTargetValue(row[column.name])"></span>
                </div>

                <!-- normal column -->
                <div v-else>
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-html="cellFormatter(row[column.name])"></span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
              </td>
              <td class="wy-table-tail"><br></td>
            </tr>
          </tbody>
        </table>

        <!-- Fixed not movable columns overlay table -->
        <table border="0" class="wy-table-fixed">
          <thead :class="(scrollLeft < -1 ? 'show-fixed-table-shadow':'regular-fixed-thead-style')">
            <tr class="wy-header">
              <th class="fixed" v-for="(group, index) in groupFixedColumns" :key="index" :colspan="group.headerSpan" 
                  v-bind:style="{'min-width': (group.mainColumn?variableColWidth:group.headerWith)+'px !important'}">
                <span v-if="group.type!='index' && group.type!='selection' && group.name !== 'target'">{{group.label}}</span>
                <span v-else-if="group.type!='index' && group.type!='selection' && tableData && tableData.length > 0 && iShowTable" >{{group.label}}</span>
                <span v-if="group.mainColumn && searchFieldName == 'response'" class="total-topic-num">Total {{totalTopicNumber}}</span>
                <span v-if="group.mainColumn && searchFieldName !== 'response'" class="total-topic-num">&nbsp;&nbsp;&nbsp;Total: {{totalTopicNumber}}</span>
              </th>
            </tr>
            <tr class="wy-sub-header">
              <th v-for="(column, index) in fixedColumns" :key="index" v-bind:class="fixedTableHeaderClasses(column)"
                  v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px !important'}"  @click="handleSort(column)" 
                  @mouseenter="toggleSortCaretHovered(column.sortBy)" @mouseleave="toggleSortCaretHovered(column.sortBy)">
                <el-checkbox v-if="column.type=='selection'" ref="headerCheckbox" v-model="selectAll"  @change.native="handleSelectAll" @click.native="handleSelectStopPropagation" :indeterminate="selectedRowsNum > 0 && selectedRowsNum < tableModel.results.length" :checked="selectedRowsNum == tableModel.results.length"/> 
                <span v-else-if="column.type!='index'">{{column.label}}</span>
              </th>
            </tr>        
          </thead>
          <tbody ref="wyTableBodyFixed" class="wy-body" v-bind:class="{'isTableEditing': isTableEditing, 'show-fixed-table-shadow': scrollLeft < -1}" @mousewheel="handleMousewheelOnFixedTable" @scroll="handleScrollingOnFixedTable">
            <tr v-for="(row, index) in tableData" :key="index" @click="handleClick($event, index, row)" @mouseover="handleMouseover" @mouseleave="handleMouseleave" :class="{'highlightedRow': index == highlightedIndex, 'clickable': isClickable}">
              <td class="fixed" v-for="(column, columnIndex) in fixedColumns" :key="columnIndex" :class="{'last-child': column.lastChild}"
                v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px', 'max-width': (column.mainColumn?variableColWidth:column.minWidth)+'px','text-align': column.align}">

                <div v-if="column.customCell">
                  <component :is="column.customCell" v-bind="{controller: controller, column: column, row: row, index: index}"></component>
                </div>

                <!-- index column -->
                <div v-else-if="column.type=='index'">
                  {{index+1}}
                </div>
                <!-- selection column -->
                <div v-else-if="column.type=='selection'" class="wy-table-selection">
                  <el-checkbox v-model="row.isSelected" @change.native="handleSelect" @click.native="handleSelectStopPropagation"/>
                </div>
                <!-- icon column -->
                <div v-else-if="column.type=='icon'">
                  <div v-if="row[column.name].icon != ''" class="wy-table-icon" v-bind:style="{'background-image': row[column.name].icon}" 
                    @mouseover="handlePopoverOver($event, row[column.name].toolTips)" @mouseout="handlePopoverOut($event)">
                    <div v-if="row[column.name].value>=1000" class="icon-number small-font">....</div>
                    <div v-else class="icon-number" v-bind:class="{'small-font': (row[column.name].value>=100 && row[column.name].value<1000)}">{{row[column.name].value}}</div>
                  </div>
                </div>
                <!-- editable column -->
                <div v-else-if="column.type=='editable'">
                  <div class="wy-table-editor" v-bind:class="{'isEditing': row.isEditing}">
                    <el-input v-if="row.isEditing" class="wy-table-content" type="text" v-model="editingValue"  @blur="handleEditingBlur($event, column, row)" @keydown="handleKeyDown"></el-input>
                    <span v-else class="wy-table-content" :title="row[column.name]">{{row[column.name]}}</span>
                    <span v-if="isRoleAllow([4201, 900011])" class="el-edit-icon el-edit-pencil" @click="handleEditing($event, column, row, true)" 
                      @mouseover="handleTooltipOver($event, 'Edit Topic Name')" @mouseout="handleTooltipOut($event)"></span>
                    <!-- <span v-if="isRoleAllow([4201, 900011])" class="el-edit-icon el-edit_confirm" @click="handleEditingConfirm($event, column, row)"></span> -->
                    <span v-if="isRoleAllow([4201, 900011])" class="el-edit-icon el-edit-cancel" @click="handleEditing($event, column, row, false)"></span>
                  </div>
                </div>
                <!-- normal column -->
                <div v-else>
                  <span v-if="column.prefix">{{column.prefix}}</span>
                  <span v-if="column.label=='Name'" :title="row[column.name]">{{row[column.name]}}</span>
                  <span v-else>

                    {{row[column.name]}}
                    </span>
                  <span v-if="column.suffix">{{column.suffix}}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="finishParentRender && !this.controller.loading" class="no-data-section">
        <table border="0" class="wy-table-main" disabled="true">
          <thead style="pointer-events: none; opacity: 0.4;">          
            <tr ref="wyHeaderMain" class="wy-header" :style="{'left': scrollLeft + 'px'}">
              <th v-for="(group, index) in groupColumns" :key="index" :colspan="group.headerSpan" 
                  v-bind:style="{'min-width': (group.mainColumn?variableColWidth:group.headerWith)+'px !important'}">
                <span v-if="group.type!='index' && group.type!='selection' && group.name !== 'target'">{{group.label}}</span>
                <span v-else-if="group.type!='index' && group.type!='selection' && tableData && tableData.length > 0 && iShowTable" >{{group.label}}</span>
              </th>
              <th class="wy-table-tail"><br></th>
            </tr>
            <tr ref="wySubheaderMain" class="wy-sub-header" :style="{'left': scrollLeft + 'px'}">
              <th v-for="(column, index) in allColumns" :key="index" 
                  v-bind:class="{'lastChild': column.lastChild, 'sortable': sortableColumn(column), 'sorted': sortedColumnName==column.sortBy,'ascending': sortedType^sortCaretHovered==0, 'descending': sortedType^sortCaretHovered==1}" 
                  v-bind:style="{'min-width': (column.mainColumn?variableColWidth:column.minWidth)+'px !important'}" @click="handleSort(column)" 
                  @mouseenter="toggleSortCaretHovered(column.sortBy)" @mouseleave="toggleSortCaretHovered(column.sortBy)">
                <span v-if="column.type!='index'">{{column.label}}</span>
              </th>
              <th class="wy-table-tail"><br></th>
            </tr>        
          </thead>
          <tbody>
            <no-data-page> </no-data-page>
          </tbody>
        </table>
      </div>
      <!-- <no-data-page v-else-if="finishParentRender && !this.controller.loading"> </no-data-page> -->
    </div>

    <el-tooltip
      ref="tableTooltip"
      placement="bottom"
      effect="dark"
      >
      <div slot="content"><span v-html="tableTooltipContent"></span></div>
    </el-tooltip>

    
    <el-popover
      ref="tablePopover"
      placement="bottom"
      title=""
      trigger="hover"      
      popper-class="wy-table-change-popover"
      :content="tablePopoverContent">
    </el-popover>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import noDataPage from '../../views/PageNoDataWithAvailableDateRange';
import wyTargetNumberRange from './wy-target-number-range';
import wyTargetOperator from './wy-target-operator';


export default {
  name: 'wy-table-list',
  props: ['controller', 'enableSelection', 'rowClickEvent', 'defaultSort', 'searchFieldName', 'configrationData',' configrationColumns', 'searchKeywordInput'],
  components: {
    'no-data-page': noDataPage,
    'wy-target-number-range': wyTargetNumberRange,
    'wy-target-operator': wyTargetOperator
  },
  data() {
    return {
      module: this,
      loading: false,
      iShowTable: false,
      groupColumns: null,
      groupFixedColumns: null,
      sortedColumnName: null,
      sortedType: 1,
      noDataTooltipContent: 'Data not available for this time period',
      noDataTooltipForPercentChange: 'Correct comparison not available for this time period',
      noDataTooltipForTarget: 'Target not available for this time period',
      sortCaretHovered: 0,
      tablePopoverContent: null,
      tableTooltipContent: null,
      isTableEditing: false,
      editingValue: null,
      highlightedIndex: null,
      scrollLeft: 0,
      selectedRowsNum: 0,
      allColsWidth: -1,
      variableColWidth: 350,
      finishParentRender: false,
      selectAll: false,
      contactReasonsData: {},
      openDropdown: null
    }
  },

  computed: {
    ...mapGetters(['dataModel', 'tableModel', 'isRoleAllow', 'companyConfig']),

    isClickable() {
      return (this.rowClickEvent!=null)
    },

    fixedColumns() {
      return this.allColumns.filter((item) => item.fixed)
    },

    allColumns() {
      let list = [];
      this.groupColumns.forEach(group => {
        let currGroup = group.columns.filter(el => el.selected);
        for (let i = 0; i < currGroup.length-1; i ++)
          currGroup[i].lastChild = false;
        if (currGroup.length)
          currGroup[currGroup.length-1].lastChild = true;
        list = list.concat(currGroup);
      })
      return list;
    },

    tableData() {
      let list = null;
      if(this.searchKeywordInput!='') {
        let keywordList = this.searchKeywordInput.split(',').map(item => {
          return item.trim().replace(/[-\\^$*+?.()|[\]{}]/g, '\\$&');
        });
        // list = this.contactReasonsData.data.filter(item => new RegExp(keywordList.join("|")).test(item.name))
        list = this.contactReasonsData.data.filter(item => new RegExp(keywordList.join("|"), 'i').test(item.name))
      }
      else {
        list = this.contactReasonsData.data;
      }

      if(this.sortedColumnName) {
        if(this.sortedType == 0) {
          list.sort((a,b) => (a[this.sortedColumnName] > b[this.sortedColumnName]) ? 1 : ((b[this.sortedColumnName] > a[this.sortedColumnName]) ? -1 : 0))
        }
        else {
          if(list.totalResponses){
            list.results.sort((a,b) => (b[this.sortedColumnName] > a[this.sortedColumnName]) ? 1 : ((a[this.sortedColumnName] > b[this.sortedColumnName]) ? -1 : 0))
          } 
          else {
            list.sort((a,b) => (b[this.sortedColumnName] > a[this.sortedColumnName]) ? 1 : ((a[this.sortedColumnName] > b[this.sortedColumnName]) ? -1 : 0))
          }
        }
      }
      if(this.contactReasonsData.targetType && (this.contactReasonsData.targetType == 'withOperator' || this.contactReasonsData.targetType == 'withReverse')) {
        return list
      }
      else {
        if(this.contactReasonsData.data&&this.contactReasonsData.data.length>0) {
          if(this.searchKeywordInput == ''){
            this.mergeConfigrationData(list, this.configrationData)
          }
          // console.log("list", list)
          return list;
        }
        else if(this.contactReasonsData.data){
          // console.log("nodata", this.contactReasonsData.data)
          return this.contactReasonsData.data
        }
        else {
          return this.contactReasonsData
        }
      }
    },

    totalTopicNumber() {
      return WyUtils.formatterNumber(this.tableData.length);
    }
  },

  watch: {
    'dataModel.keyword': function() {
      this.clearSelection();
    }
  },

  mounted: function() {
    this.resizeObserver = new ResizeObserver(this.handleWindowResize).observe(this.$refs.wyTableList);
  },

  updated: function() {
    this.handleWindowResize();
  },  

  methods: {
    ...mapMutations(['updateTableModelSelectedRows']),

    cellFormatter(value) {
      if(typeof value == "number") {
        value = WyUtils.formatterNumber(value);
      }
      return value;
    },

    formatNumberWithTwoZero(val) {
      if(val == 0) {
        return '00.00';
      }
      else {
        return val;
      }
    },

    fixedTableHeaderClasses(column) {
      let classes = {'lastChild': column.lastChild, 'sortable': column.sortable, 'fixed': true, 'sorted': this.sortedColumnName==column.sortBy, 
          'ascending': this.sortedType^this.sortCaretHovered==0, 'descending': this.sortedType^this.sortCaretHovered==1};
      classes[column.name] = true;
      return classes;
    },

    checkGreaterThanThreshold(row) {
      if(this.companyConfig && this.companyConfig.appPages && this.companyConfig.appPages.nluIntentPerformancePage && this.companyConfig.appPages.nluIntentPerformancePage["thresholdOfTotalIssues"]) {
        let threshold = this.companyConfig.appPages.nluIntentPerformancePage["thresholdOfTotalIssues"]
        if(row.issuesPercent && row.issuesPercent > threshold) {
          return true
        }
        else {
          return false
        }
      }
      else {
        return false
      }
    },

    handleSort({sortBy, sortable, sortType}) {
      if(sortable) {
        if(sortType != null) {
          this.sortedColumnName = sortBy;
          this.sortedType = sortType;
          return;
        }
        if(this.sortedColumnName != sortBy) {
          this.sortedType = 1;  
        }
        else {
          this.sortedType = this.sortedType==0 ? 1 : 0;
        }
        if (this.sortedColumnName != sortBy) {
          this.sortedColumnName = sortBy;
          this.sortCaretHovered = 1;
        }
      }
    },

    handleSelect(e) {
      e.preventDefault();
      e.stopPropagation();
      let selectedRows = this.tableData.filter(item => item.isSelected);
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = selectedRows.length;

      if(this.tableData.length == this.selectedRowsNum) {
        this.selectAll = true;
      }

      if(this.selectedRowsNum == 0) {
        this.selectAll = false;
      }
    },

    handleSelectStopPropagation(e) {
      e.stopPropagation();
    },

    handleSelectAll(e) {
      e.preventDefault();
      e.stopPropagation();
      let selectedRows = this.tableData.filter(item => {item.isSelected=e.target.checked; return item.isSelected});
      this.updateTableModelSelectedRows(selectedRows);
      this.selectedRowsNum = selectedRows.length;
    },

    clearSelection() {
      if(this.tableData.filter) {
        let selectedRows = this.tableData.filter(item => {item.isSelected=false; return false});
        this.updateTableModelSelectedRows(selectedRows);
        this.selectedRowsNum = 0;
      }
      
      
      this.selectAll = false;
    },

    handlePopoverOver(e, content) {
      this.$refs.tablePopover.doClose();
      this.tablePopoverContent = content;
      this.$refs.tablePopover.referenceElm = e.target;
      setTimeout(() => {
        this.$refs.tablePopover.doShow();
      }, 100)
    },

    handlePopoverOut() {
      this.tablePopoverContent = null;
      this.$refs.tablePopover.referenceElm = null;
      setTimeout(() => {
        this.$refs.tablePopover.doClose();
      }, 10)
    },

    handleTooltipOver(e, content) {
      if(this.$refs.tableTooltip){
        this.$refs.tableTooltip.hide();
        this.$refs.tableTooltip.handleClosePopper();
        this.$refs.tableTooltip.referenceElm = e.target;
        
        this.$refs.tableTooltip.$refs.popper.style.background = "#0C2837";
        this.$refs.tableTooltip.$refs.popper.style.fontSize = "14.16px";
      }
      this.tableTooltipContent = content;
      setTimeout(() => {

        if(this.$refs.tableTooltip){
          this.$refs.tableTooltip.show();
          this.$refs.tableTooltip.handleShowPopper();

          setTimeout(()=>{

            if (this.$refs.tableTooltip.$refs.popper.getBoundingClientRect().x < 0 || Math.abs(e.y - this.$refs.tableTooltip.$refs.popper.getBoundingClientRect().y) > 33) {
              this.$refs.tableTooltip.hide();
              this.$refs.tableTooltip.handleClosePopper();
            }
          }, 0)
        }
      }, 0)
    },

    handleTooltipOut() {
      if(this.$refs.tableTooltip){
        this.$refs.tableTooltip.hide();
        this.$refs.tableTooltip.handleClosePopper();
      }
    },

    handleMousewheelOnMainTable(e) {
      e.preventDefault();
      e.stopPropagation();
      this.$refs.wyTableBodyFixed.scrollTop += e.deltaY;  // y scroll fixed overlay table
      this.$refs.wyTableBodyMain.scrollTop += e.deltaY;  // y scroll main table
      this.$refs.wyTableBodyMain.scrollLeft += e.deltaX;  // x scroll main table
      this.scrollLeft = Math.max(0, this.$refs.wyTableBodyMain.scrollLeft) * -1;  // x scroll main header
    },

    handleMousewheelOnFixedTable(e) {
      e.preventDefault();
      e.stopPropagation();
      this.$refs.wyTableBodyFixed.scrollTop += e.deltaY;  // y scroll fixed overlay table
      this.$refs.wyTableBodyMain.scrollTop += e.deltaY;  // y scroll main table
    },

    handleScrollingOnMainTable(e) {
      // x scroll fixed overlay table with main table
      this.scrollLeft = e.target.scrollLeft * -1;
      // y scroll fixed overlay table with main table
      this.$refs.wyTableBodyFixed.scrollTop = e.target.scrollTop;
      setTimeout(()=>{
        document.querySelectorAll(".el-tooltip__popper.is-dark").forEach(el=>{
          el.style.display = 'none';
        })
      }, 150)
      
      if(this.openDropdown && this.openDropdown.$refs.operatorDropdown) {
        this.openDropdown.$refs.operatorDropdown.$el.querySelector(".operator-type").style.display = "none";
        this.openDropdown.$refs.operatorDropdown.blur();
        this.openDropdown = null;
      }
    },

    handleScrollingOnFixedTable(e) {
      // Handles the case where the fixed table is scrolled when user searchers keyword using Ctrl + F
      this.$refs.wyTableBodyMain.scrollTop = e.target.scrollTop;
    },

    handleClick(e, rowIndex, row) {
      let target = e.target;
      if(this.rowClickEvent && !this.isTableEditing && !target.classList.contains("wy-table-select") && !target.parentNode.classList.contains("wy-table-select")) {
        this.rowClickEvent(rowIndex, row);
      }
    },

    handleEditing(e, column, row, isEditing) {
      e.preventDefault();
      e.stopPropagation();
      row.isEditing = isEditing;
      this.isTableEditing = isEditing;
      this.editingValue = isEditing ? row[column.name] : null;
      
      setTimeout(() => {
        let inputEl = e.target.parentNode.querySelector("input.wy-table-content");
        if (inputEl && inputEl.setSelectionRange) {
            inputEl.setSelectionRange(0, 0);
            inputEl.focus();
        }
      }, 100)
    },

    handleEditingConfirm(e, column, row) {
      e.preventDefault();
      e.stopPropagation();
      if(this.editingValue) {
        row.isEditing = false;
        this.isTableEditing = false;
        if(row[column.name] != this.editingValue.trim()) {
          this.controller.updateTableData(row, column.name, this.editingValue.trim());
        }        
      }
    },

    handleEditingBlur(e, column, row) {
      e.preventDefault();
      e.stopPropagation();
      setTimeout(() => {
        if(row.isEditing && this.editingValue) {
          this.handleEditingConfirm(e, column, row);
        }
      }, 200)
    },

    handleKeyDown(event) {
      if(event.keyCode === 13) {
        event.target.blur();
      }
    },

    handleMouseover() {
      // this.highlightedIndex = e.target.closest('tr').sectionRowIndex;
    },

    handleMouseleave() {
      // this.highlightedIndex = null;
    },

    toggleSortCaretHovered(columnName) {
      if (columnName == this.sortedColumnName)
        this.sortCaretHovered = 1 - this.sortCaretHovered;
    },

    refreshTable(options) {
      // preapare selected columns
      // this.handleSort({sortBy: this.defaultSort.name, sortType: this.defaultSort.type, sortable: true});
      let groupMap = {};
      let tableColumns = []
      if(this.controller.tableColumns) {
        tableColumns = this.controller.tableColumns
      }
      else {
        if(this.controller.overviewTableColumns) {
          tableColumns = this.controller.overviewTableColumns
        }
      }
      tableColumns.forEach(column => {
        column.sortBy = column.sortBy || column.name;
        if(column.groupHidden) {
          if(column.selected) {
            groupMap[column.group] = column;
          }
        }
        else {
          if(!groupMap[column.group]) {
            groupMap[column.group] = {
              label: column.group,
              name: column.group.replace(' ', '-').toLowerCase(),
              columns: [],
              type: column.type,
              isIndeterminate: false,
              fixed: column.fixed,
              mainColumn: column.mainColumn,
              width: column.minWidth,
              checked: false
            };
          }

          if(column.selected) {
            groupMap[column.group].columns.push(column);
            // // add children
            // if(!column.parent) {
            //   this.dataModel.columns.filter(el => el.parent==column.name).forEach(childColumn => {
            //     groupMap[column.group].columns.push(childColumn);
            //   });
            // }
          }
        }        
      });
      let groupList = [];
      let groupFixedList = [];
      Object.keys(groupMap).forEach(key => {
        let group = groupMap[key];
        if(group.columns == undefined || group.columns.length > 0) {
          // prepare selected colum list
          groupList.push(group);
          // calculate the header width and column spans
          if(group.columns.length > 1) {
            group.headerWith = group.columns.reduce((total, item, index) => {
              total+=item.minWidth+(index>0?20:0); 
              return total;
            }, 0);
          }
          else {
            //If there is only 1 field in this group, group header and field header min-width = 180px
            group.headerWith = ((group.headerWith > 180) || (100 > group.headerWith)) ? group.headerWith : 180; 
            group.columns.reduce((total,item) => {
              item.minWidth = (item.minWidth < 100 || item.minWidth > 180) ? item.minWidth : 180;
              group.headerWith = item.minWidth;
            }, 0);
          }
          group.headerSpan = group.columns.filter(el => el.selected).length;
          
          // prepare fixed column list
          let fixedColumns = group.columns.filter((item) => item.fixed);
          if(fixedColumns.length > 0) {
            groupFixedList.push(group)
          }
        }
      });

      this.allColsWidth = 0;
      for (let i = 0; i < groupList.length; i ++) {
        if (!groupList[i].mainColumn)
          this.allColsWidth += groupList[i].headerWith + 20;
      }
      // this.variableColWidth will be updated in updated() method
      // this.controller.loading = true;
      // if(!options || !options.disablePageLoading) {
      //   this.controller.$router.app.setLoading(true);
      // }
      this.iShowTable = false;
      setTimeout(() => {
        this.controller.loading = false;
        if(!options || !options.disablePageLoading) {
          this.controller.$router.app.setLoading(false);
        }
        this.groupColumns = groupList;
        this.groupFixedColumns = groupFixedList;
        this.iShowTable = true; 
        this.scrollLeft = 0;
        this.finishParentRender = true;
      }, 200);

      // this.clearSelection();
    },

    handleWindowResize() {
      if(this.$refs.wyTableBodyMain) {
        this.$refs.wyTableBodyFixed.style.height = this.$refs.wyTableBodyMain.clientHeight + "px";
        if (this.allColsWidth >= 0) {
          // The next four lines of code are same as this.variableColWidth = Math.max(350, this.$refs.wyTableBodyMain.clientWidth - this.allColsWidth - 100);
          // except it prevents an oscillation phenomenon in Chrome when there are no other columns and we toggle the sidebar
          // This is done by only updating the variableColWidth when it's different from two steps ago
          let tmp = Math.max(350, this.$refs.wyTableBodyMain.clientWidth - this.allColsWidth - 55);
          if (tmp != this.variableColWidthPrev) {
            this.variableColWidthPrev = this.variableColWidth;
            this.variableColWidth = tmp;
          }
        }
      }
    },

    sortableColumn(column) {
      /*
      * "% change from last period" column sorting should be disabled 
      * if every data returned in this column is:
      * 1. null
      * 2. -1000000 (/src/store/modules/analytics.js - generateContactReasonsData())
      * 3. 0 (for NLU Intent Performance Page)
      */
      if (column.sortable && column.noDataSortable) {
        const sortByColumn = column.sortBy
        const noData = this.tableData.every(row => {
          return row[sortByColumn] == null || row[sortByColumn] == -1000000 || row[sortByColumn] == 0
        })
        if (noData) {
          return false
        } else {
          return true;
        }
      } else {
        return column.sortable;
      }
    },

    mergeConfigrationData(tableData, configrationData) {
      if(tableData && tableData.length > 0) {
        let list = [];
        let map = {};
        configrationData.forEach(item => {
          list.push(item.name)
          map[item.name] = item;
        })
        tableData.forEach(contactReason => {
          if(list.includes(contactReason.name)) {
            contactReason.besTarget = map[contactReason.name].target.bes;
            contactReason.basTarget = map[contactReason.name].target.bas;
          }
          else {
            contactReason.besTarget = {"value": null};
            contactReason.basTarget = {"value": null};
          }
        })
      }
      this.contactReasonsData.data = tableData;
    },

    handleReverseChange(e, target) {
      target.reverse = e.target.checked;
      this.controller.controller.hasNoSavedChanges = true;
    },

    removeTargetValue(data, ref) {
      if(ref) {
        if(typeof this.controller.isEditing != 'undefined') {
          this.controller.isEditing = true;
        }
        if(ref[0].localData.value || ref[0].localData.value == 0) {
          ref[0].localData.value = null
          data.value = null
        }
      }
      else {
        if(data.operator) {
          data.operator = '-';
        }
        if(data.value) {
          data.value = null
          if(this.controller.controller && typeof this.controller.controller.hasNoSavedChanges != 'undefined') {
            this.controller.controller.hasNoSavedChanges = true
          }
        }
        if(data.reverse) {
          data.reverse = false;
        }
      }
      
      setTimeout(() => {
        if(typeof this.controller.controller.hasError != 'undefined') {
          this.controller.controller.hasError = document.querySelectorAll('.has-error').length > 0;
        }
      }, 200);
    },

    removeContactReasonTargetValue(data, tableRowData) {
      // if(data.operator) {
      //   data.operator = '-';
      // }
      if(data.value) {
        data.value = ''
        tableRowData.value = ''
        if(this.controller.controller && typeof this.controller.controller.hasNoSavedChanges != 'undefined') {
          this.controller.controller.hasNoSavedChanges = true
        }
      }
      setTimeout(() => {
        if(typeof this.controller.controller.hasError != 'undefined' && !this.controller.controller.hasError) {
          this.controller.controller.hasError = document.querySelectorAll('.has-error').length > 0;
        }
      }, 200);
    },

    generateData(data) {
      return {
        value: data.value
      }
    }
  }
}
</script>

<style lang="scss">
.el-tooltip__popper.is-dark.percentage-tooltip-style{
  font-size: 14px !important;
  background: #0C2837 !important;
  width: 167px;
  font-family: 'Helvetica Neue', Arial, Helvetica, sans-serif !important;
}
.wy-table-list.targets {
  width: 100%;

  .wy-body .reverse-text {
    padding-right: 5px;
    padding-top: 9px;
  }

  .wy-body {
    span.cancel-target {
      line-height: 2.3;
      margin-left: 5px;
      cursor: pointer;
    }
    span.company-target-suffix {
      line-height: 1.3;
      margin-left: 5px;
      width: 20px;
    }
  }

  .target-red-style {
    background-color: #FBE4E3;
    width: 40px;
    font-size: 13px;
    height: 18px;
    color: #C11149;
    display: flex;
    align-items: center;
    padding-left: 15px;
    margin-left: 35%;
    background-size: 30%;
    background-position-y: 2px;
    background-position-x: 2px;
    border-radius: 15px;
    background-repeat: no-repeat;
    background-image: url('../../assets/icons/icon_target_red.svg')!important;
  }
  .target-no-color {
    background-color: transparent;
    width: 48px;
    font-size: 13px;
    height: 18px;
    padding-left: 3px;
    margin-left: 35%;
    color: var(--site-font-chart-color);
    background-size: 30%;
    background-position-y: 2px;
    margin-right: 10px;
    background-position-x: 0px;
    border-radius: 15px;
    background-repeat: no-repeat;
    //background-image: url('../../../assets/icons/icon_target_red.svg')!important;
  }
  .target-yellow-style {
    background-color: #FBFCDB;
    width: 40px;
    font-size: 13px;
    height: 18px;
    color: #8A5608;
    background-size: 30%;
    display: flex;
    padding-left: 15px;
    margin-left: 35%;
    align-items: center;
    background-position-y: 2px;
    background-position-x: 2px;
    border-radius: 15px;
    background-repeat: no-repeat;
    background-image: url('../../assets/icons/icon_target_yellow.svg')!important;
  }
  .target-green-style {
    background-color: #D9EEDF;
    width: 40px;
    font-size: 13px;
    height: 18px;
    padding-left: 15px;
    margin-left: 35%;
    display: flex;
    align-items: center;
    color: #186731;
    background-size: 30%;
    background-position-y: 2px;
    background-position-x: 2px;
    border-radius: 15px;
    background-repeat: no-repeat;
    background-image: url('../../assets/icons/icon_target_green.svg')!important;
  }

  .wy-table-wrapper {
    position: relative;

    border-top-left-radius: 15px;
    border-top-right-radius: 15px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;

    background-color: white;
  }

  table {    
    border-spacing: 0px;
    border-collapse: separate;

    font-size: 14px;
  }

  thead {
    display: table;
    table-layout:fixed;
    background-color: #F4F6F8;
    padding: 0px;

    tr{
      display: block;
      th {
        text-align: left;
        color: #214586;
        padding: 9px 10px;
        height: 19px;
        min-height: 19px;
      }
    }

    tr.wy-header, tr.wy-sub-header {
      > th:first-child {
        min-width: 34px !important;
      }
    }

    .wy-header {
      position: relative;
      display: none;
      left: 0px;

      font-size: 15px;
      font-weight: 500;

      th {
        border-right: 4px solid #fff;

        vertical-align: top;
        text-align: center;
      }
    }

    .wy-sub-header {
      position: relative;
      left: 0px;

      th {
        font-size: 13px;
        vertical-align: top;
        text-align: center;
      }
      th.lastChild {
        border-right: 4px solid #fff;
      }
      th.fixed.index label.el-checkbox {
        position: relative;
        bottom: -1px;
      }
      th.sortable::after {  // this reserves space for the up-arrow/down-arrow icon (not shown yet)
        content: "";
        display: inline-block;
        height: 15px;
        width: 15px;
        position: relative;
        top: 4px;
        left: 4px;
        background-repeat: no-repeat;
      }
      th.sortable.sorted.ascending::after {
        background-image: url('../../assets/icons/icon_sort_asc.svg');
      }
      th.sortable.sorted.descending::after {
        background-image: url('../../assets/icons/icon_sort_des.svg');
      }
      th.sortable.sorted.ascending:hover::after {
        background-image: url('../../assets/icons/icon_hover_arrow_up.svg');
      }
      th.sortable.sorted.descending:hover::after {
        background-image: url('../../assets/icons/icon_hover_arrow_down.svg');
      }
      th.sortable:hover {
        background-color: transparent;
        // background-color: #E1E6EC;
        cursor: pointer;
        color: #214586;
        .sort-caret.ascending {
          background-image: url('../../assets/icons/icon_hover_arrow_up.svg') !important;
        }
        .sort-caret.descending {
          background-image: url('../../assets/icons/icon_hover_arrow_down.svg');
        }
      }
      th.sortable:not(.sorted):hover::after {
        background-image: url('../../assets/icons/icon_hover_arrow_down.svg');
      }
      th:not(.sortable) {
        span {
          padding-left: 0;
        }
      }
    }
  }

  tbody.isTableEditing {
    tr {
      cursor: default !important;
      .el-edit-pencil {
        display: none !important;
      }
    }
  }
  
  tbody {
    display: block;

    tr {
      table-layout: fixed;
      display: block;
      cursor: default;

      td {
        padding: 1px 10px;
        height: 60px;
        border-bottom: 1px solid #eee;
        background-color: transparent;

        div {
          display: flex;
          justify-content: center;
        }
      }

      .wy-table-select .el-checkbox__original {
        margin: 0;
      }

      td.last-child {
        border-right: 4px solid transparent;
      }

      td.wy-table-tail {
        padding: 0;
      }
    }

    tr.clickable {
      cursor: pointer;
    }

    .highlightedRow td.fixed.last-child {
      background-color: #f5f7fa !important;
    }
  }

  .highlightedRow td {
    background-color: #f5f7fa !important;
  }

  .wy-table-select {
    min-width: 20px;
    position: relative;
  }

  .wy-table-tail {
    min-width: 22px;
  }

  .wy-table-main {
    width: 100%;
    max-width: 100%;
    thead {
      width: fit-content;
      th {
        background-color: #F4F6F8;
      }
      th:last-child, th:nth-last-child(2) {
        border-right: 4px solid transparent;
      }
      tr {
        // float: left;  // THIS PLACE IS WHERE SAFARI MESSED UP WHEN YOU ZOOM OUT THE PAGE (BUT CHROME AND FIREFOX DIDN'T)
        width: calc( 100vw - 350px );
        :first-child {
          padding-left: 18px;  // adding the same amt of padding as main column in fixed table
        }
      }
      .wy-header {
        span {
          padding-left: 5px;
        }
      }
    }
    tbody {      
      height: calc( 100vh - 479px );
      overflow: auto;
      // overflow-y: scroll;
      width: 100%;
      // max-width: 80%;
      tr {
        // float: left;  // THIS PLACE IS WHERE SAFARI MESSED UP WHEN YOU ZOOM OUT THE PAGE (BUT CHROME AND FIREFOX DIDN'T)
        width: calc( 100vw - 700px );
        td:first-child {
          padding-left: 20px;  // adding the same amt of padding as main column in fixed table
        }
        td .positive-change-style {
          background-color: #F4F4FC;
          border: 1px solid #F4F4FC;
          color: #6446BB;
          padding-left: 24px;
          padding-right: 15px;
          font-weight: 400;
          border-radius: 30px;
          line-height: 34px;
          text-align: right;
          display: flex;
          font-size: 14px;
          height: 30px;
          width: fit-content;
          align-items: center;
          margin: auto;
          
        }
        td .negative-change-style {
          background-color: #EFFCFF;
          border: 1px solid #EFFCFF;
          color: #3961AE;
          padding-left: 24px;
          padding-right: 15px;
          border-radius: 30px;
          font-weight: 400;
          line-height: 34px;
          text-align: right;
          display: flex;
          align-items: center;
          font-size: 14px;
          height: 30px;
          width: fit-content;
          margin: auto;
        }
        .arrow-up {
          background: url('../../assets/icons/icon_performance_arrow_up.svg') no-repeat;
          background-position-y: 7.87px;
          background-position-x: 10px;
          background-size: 11.95px;
        }
        .arrow-down {
          background: url('../../assets/icons/icon_performance_arrow_down.svg') no-repeat;
          background-position-y: 7.87px;
          background-position-x: 10px;
          background-size: 11.95px;
        }

        td .no-data-tooltip-style, td .zero-change-style {
          background-color: #E4E8EC;
          border: 1px solid #E4E8EC;
          border-radius: 30px;
          color: var(--site-font-tertiary-color);
          padding-left: 24px;
          padding-right: 24px;
          align-items: center;
          font-weight: 400;
          line-height: 34px;
          text-align: right;
          display: flex;
          font-size: 14px;
          height: 30px;
          width: fit-content;
          margin: auto;
        }
        td span.positive-change {
          color: #4448A2;
          margin-left: 4px;
        }
        td span.negative-change {
          color: #3E6891;
          margin-left: 4px;
        }
        td span.zero-change {
          color: var(--site-font-tertiary-color);
        }
      }
      border-right: 9px solid #eee;
      border-bottom: 1px solid #eee;
    }
  }

  .wy-table-fixed {
    position: absolute;
    top: 0px;
    text-align: center;
    border-left: 1px solid #eee;
    //z-index: 2;
    thead {
      th:nth-last-child(1) {
        padding-left: 30px;
        text-align: left !important;
      }
    }
    tbody {
      max-height: calc( 100vh - 492px);
      height: 100%;
      overflow: auto;
      -ms-overflow-style: none;  // hiding scrollbar on IE
      scrollbar-width: none;     // hiding scrollbar on Firefox
                                 // see below for Chrome
      width: fit-content;
      td:nth-last-child(1) {
        padding-left: 30px;
        font-weight: 500;
        padding-right: 3px;
        div {
          justify-content: flex-start;
        }
      }

      tr td:first-child {
        background-color: #F7F8F8;
      }

      .wy-table-selection {
        padding-left: 6px;
      }
    }
    tbody.show-fixed-table-shadow {
      box-shadow: 5px -2px 10px -10px grey;
      background-color: white;
    }
    tbody::-webkit-scrollbar {    // hiding scrollbar on Chrome
      display: none;
    }
    input {
      margin: 3px;
    }
    .total-topic-num {
      font-size: 12px;
    }
  }

  .el-no-data {
    cursor: pointer;
    padding: 5px 10px;
    border-radius: 30px;
    background-color: #E4E8EC;
  }

  .is-action-item.red {
    cursor: pointer;
    background-image: url('../../assets/icons/icon_under_trained_intent_red.svg') !important;
    // background-size: 75%;
    background-repeat: no-repeat;
    background-position-y: 2px;
    background-position-x: 1px;
    background-color: transparent !important;
    height: 20px;
    width: 20px;
    border: 0px;
    margin-left: 0px;
    background-size: 88%;
  }
  
  .is-action-item.yellow {
    cursor: pointer;
    background-image: url('../../assets/icons/icon_under_trained_intent_yellow.svg') !important;
    // background-size: 75%;
    background-repeat: no-repeat;
    background-position-y: 2px;
    background-position-x: 1px;
    background-color: transparent !important;
    height: 20px;
    width: 20px;
    border: 0px;
    margin-left: 0px;
    background-size: 88%;
  }

  .icon-number {
    background-color: #80a6cd;
    border-radius: 10px;
    border: 1px solid #FFF;
    height: 16px;
    width: 16px;
    position: relative;
    top: -10px;
    left: 8px;
    font-size: 9px;
    padding-top: 1px;
    padding-left: 0px;
    line-height: initial;
    color: #FFF;
    text-align: center;
  }

  .small-font {
    font-size: 6px !important;
    padding-top: 3px !important;
  }

  .wy-table-icon {
    display: inline-block;
    width : 25px;
    height: 25px;
    background-repeat: no-repeat;
    background-size: 55%;
    position: relative;
    top: 10px;
  }

  .wy-table-change-popover.el-popover--plain {
      padding: 8px 10px;
      border-radius: 5px;
      background-color: var(--site-font-chart-color);
      color: #FFF;
      font-size: 12px;
    .popper__arrow {
      filter: invert(1);
    }
  }

  .wy-table-alert-popover.el-popover--plain {
    background: #80A6CD;
    color: #FFF;
    border-radius: 10px;
    padding: 5px 10px;
    display: inline-block;
    .popper__arrow {
      display: none;
    }
  }

  .wy-table-editor {
    padding: 0;
    width: 100%;
    display: flex;
    align-items: center;
  }

  .wy-table-editor.isEditing {
    pointer-events: none;
    .el-edit-cancel {
      display: inline-block;
      width: 63px;
      color: #106FBC;
    }
    .el-edit_confirm {
      display: inline-block;
    }
    .el-edit_confirm::after {
      content: '|';
      font-size: 20px;
      font-weight: 200;
      color: #ddd;
      text-shadow: 0 0 2px #ddd;
      position: absolute;
      right: -2px;
      top: 1px;
    }
    .wy-table-content {
      display: inline-block;
      width: 255px;
      padding: 5px 10px;
      margin-right: -10px !important;
      pointer-events: initial;
    }
    .el-input__inner {
      background-color: #FFF;
      border-radius: 4px;
      position: relative;
      right: 12px;
      height: 34px;
      &:focus-within{
        border: 1px solid #106FBC;
      }
    }
    input.el-input__inner {
      border-width: 1px !important;
    }
  }

  .highlightedRow {
    .el-edit-pencil {
      display: block;
    }
  }

  .wy-table-content {
    display: inline-block;
    border: 1px solid transparent;
    vertical-align: middle;
    margin-right: auto;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .el-edit-icon  {
    display: none;
    cursor: pointer;
    background-size: 16px;
    background-repeat: no-repeat;
    background-position-y: 7px;
    background-position-x: 7px;
    background-color: transparent;
    border-radius: 16px;
    height: 30px;
    width: 30px;
    position: relative;
    left: 5px;
    top: 0px;
    vertical-align: middle;
    pointer-events: initial;
  }

  .el-edit-pencil {
    background-image: url('../../assets/icons/icon_edit_pencil.svg') !important;
    
  }
  .el-edit-pencil:hover {
    background-color: #ebf4fd;
  }

  .el-edit-cancel {
    // background-image: url('../../assets/icons/icon_topic_cancel.svg') !important;
  }
  .el-edit-cancel:hover {
    background-color: unset!important;
  }

  .el-edit_confirm {
    background-image: url('../../assets/icons/icon_topic_confirm.svg') !important;
  }
  .el-edit_confirm:hover {
    background-color: #ebf4fd !important;
  }
}
</style>